/**
 * アクセスの計測（GA4。docs/02 §2.1.2）。
 *
 * <b>測定 ID は書き出しが <c>&lt;meta name="ga4-measurement-id"&gt;</c> に入れる</b>。入れるのは本番の書き出し（main）だけで、
 * 手元・E2E・PR のプレビューには無い——本番の数字を汚さない。無ければ何もしない。
 *
 * <b>インラインの <c>&lt;script&gt;</c> は書かない</b>（CSP）。Google のタグは、ここから読み込む。
 * 読み込み先は <c>www.googletagmanager.com</c>。
 *
 * <b>送る URL から <c>?</c> と <c>#</c> の後ろを落とす。</b>この製品の URL には、招待の合言葉やメールの確認コードが載る。
 * 画面を移るたびの計測（会員の画面は React が履歴を書き換える）は GA4 の拡張計測機能が送るので、
 * GA4 の管理画面の「データの削除」で、同じ問い合わせの項目（passphrase・code・userId・email）も伏せる（docs/09 B-7）。
 */

/** 測定 ID の形（<c>G-</c> と英大文字・数字）。 */
export const measurementIdPattern = /^G-[A-Z0-9]+$/u;

/** 送る URL。<b>問い合わせ（<c>?</c>）と断片（<c>#</c>）を落とす</b>。 */
export const sanitizedLocation = (href: string): string => {
    const url = new URL(href);
    return url.origin + url.pathname;
};

interface AnalyticsWindow {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
}

/** GA4 を読み込む。測定 ID が無い（本番以外）・形が違うなら何もしない。 */
export function setupAnalytics(doc: Document = document, win: Window = window): void {
    const id = doc.querySelector<HTMLMetaElement>("meta[name=\"ga4-measurement-id\"]")?.content ?? "";
    if (!measurementIdPattern.test(id)) {
        return;
    }

    const target = win as Window & AnalyticsWindow;
    const layer = (target.dataLayer ??= []);
    // Google のタグは arguments そのもの（配列ではない）を受け取る決まり
    target.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params -- Google のタグの約束どおり arguments を積む
        layer.push(arguments);
    };
    target.gtag("js", new Date());
    target.gtag("config", id, {
        page_location: sanitizedLocation(win.location.href),
        page_referrer: doc.referrer === "" ? "" : sanitizedLocation(doc.referrer),
    });

    const script = doc.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    doc.head.append(script);
}
